// Inline form for adding a user-origin term to a section. The added term
// gets `origin: "user"` + `action: "added"` per plan/02_review_ui.md.

import { useState } from "react";
import type { TermKind } from "../data/types";
import { useReviewStore } from "./store";

interface Props {
  sectionId: string | null;
  /** Optional callback fired with the new term_id once added. */
  onAdded?: (termId: string) => void;
}

const KINDS: TermKind[] = ["concept", "acronym", "type", "error", "param", "resource", "other"];

export function AddTermForm({ sectionId, onAdded }: Props) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [definition, setDefinition] = useState("");
  const [kind, setKind] = useState<TermKind>("concept");
  const addTerm = useReviewStore((s) => s.addTerm);

  const reset = () => {
    setTerm("");
    setDefinition("");
    setKind("concept");
  };

  const submit = () => {
    if (!term.trim() || !definition.trim()) return;
    const created = addTerm({
      term: term.trim(),
      definition: definition.trim(),
      alternates: [],
      kind,
      source_block_id: sectionId,
    });
    reset();
    setOpen(false);
    onAdded?.(created.term_id);
  };

  if (!open) {
    return (
      <button
        type="button"
        className="maisight-review-add-toggle"
        onClick={() => setOpen(true)}
        aria-label={`Add a term${sectionId ? ` to ${sectionId}` : ""}`}
        data-section-id={sectionId ?? ""}
      >
        + Add a term{sectionId ? ` to ${sectionId}` : ""}
      </button>
    );
  }

  return (
    <div
      className="maisight-review-add-form"
      role="form"
      aria-label="Add a term"
      data-section-id={sectionId ?? ""}
    >
      <label className="maisight-review-field">
        <span className="maisight-review-field-label">Term</span>
        <input
          className="maisight-review-input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          autoFocus
          aria-label="Term"
        />
      </label>
      <label className="maisight-review-field">
        <span className="maisight-review-field-label">Definition</span>
        <textarea
          className="maisight-review-textarea"
          value={definition}
          onChange={(e) => setDefinition(e.target.value)}
          rows={2}
          aria-label="Definition"
        />
      </label>
      <label className="maisight-review-field">
        <span className="maisight-review-field-label">Kind</span>
        <select
          className="maisight-review-input"
          value={kind}
          onChange={(e) => setKind(e.target.value as TermKind)}
          aria-label="Kind"
        >
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </label>
      <div className="maisight-review-editor-actions">
        <button
          type="button"
          className="maisight-review-btn maisight-review-btn--ghost"
          onClick={() => { reset(); setOpen(false); }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="maisight-review-btn maisight-review-btn--primary"
          onClick={submit}
          disabled={!term.trim() || !definition.trim()}
        >
          Add
        </button>
      </div>
    </div>
  );
}
